import { type HealthResponse, healthResponseSchema } from '@ofmos/shared/health';

/**
 * The single place the browser talks to the API. Responses are validated against
 * the shared zod contract, so a server change that breaks the shape fails here
 * with a clear error instead of rendering `undefined` somewhere downstream.
 */

export class ApiError extends Error {
  override readonly name = 'ApiError';
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
  }
}

export async function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  let response: Response;
  try {
    response = await fetch('/health', {
      headers: { accept: 'application/json' },
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new ApiError(`API unreachable: ${String(cause)}`);
  }

  if (!response.ok) {
    throw new ApiError(`API returned ${response.status}`, response.status);
  }

  const parsed = healthResponseSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ApiError('API returned an unexpected payload for /health');
  }
  return parsed.data;
}

/**
 * INV-13 FRONTEND CACHE DISCIPLINE. The key factory moved to
 * `src/data/queryKeys.ts` when the record keys arrived, so there is still
 * exactly ONE place where keys are built. Re-exported here because this module
 * is the HTTP surface and its callers expect the pair together.
 */
export { queryKeys } from '../data/queryKeys';
